import { PERMISSIONS, usePermissions, useSiteConfig } from '@bms/common';
import type { GrmsSiteSettings, HousekeepingStatus } from '@bms/common';
import { resolveGrmsIndicators, type GrmsIndicatorFeatures } from './grmsIndicators';

interface RoleAssignment {
  roleCode: string;
  siteCode: string | null;
}

interface UserWithRoleAssignments {
  id?: string;
  roleAssignments?: RoleAssignment[];
}

interface HousekeepingRoomPriority {
  roomCode: string;
  murActive?: boolean;
  dndActive?: boolean;
}

interface LiveHousekeepingPriority {
  murActive?: boolean;
  dndActive?: boolean;
}

export interface HousekeepingSettings {
  inspectionRequired: boolean;
}

function activeRoleCodes(
  user: UserWithRoleAssignments | null | undefined,
  siteCode: string | null | undefined,
): Set<string> {
  if (!user || !siteCode) return new Set();

  return new Set(
    (user.roleAssignments ?? [])
      .filter((assignment) => assignment.siteCode === null || assignment.siteCode === siteCode)
      .map((assignment) => assignment.roleCode),
  );
}

export function resolveHousekeepingSettings(
  settings: Record<string, unknown> | null | undefined,
): HousekeepingSettings {
  const grms = settings?.grms;
  if (!grms || typeof grms !== 'object' || Array.isArray(grms)) {
    return { inspectionRequired: true };
  }

  const housekeeping = (grms as GrmsSiteSettings).housekeeping;
  return {
    inspectionRequired: typeof housekeeping?.inspection_required === 'boolean'
      ? housekeeping.inspection_required
      : true,
  };
}

/** Reads the schema-backed site setting; missing or malformed values stay conservative. */
export function useHousekeepingSettings(): HousekeepingSettings {
  const { data: siteConfig } = useSiteConfig();
  return resolveHousekeepingSettings(siteConfig?.settings);
}

export function getCleaningCompletionStatus(inspectionRequired: boolean): HousekeepingStatus {
  return inspectionRequired ? 'to_inspect' : 'ready';
}

export interface HousekeepingAccess {
  /** `housekeeping:assign` — the row's attendant picker and the batch Assign. */
  canAssign: boolean;
  /** Holds `housekeeping:write` but not `housekeeping:assign`: "My Rooms" only. */
  isAttendantMode: boolean;
  /**
   * Holds `housekeeping:assign` but not `grms:write` (the superattendant, R3):
   * home is Housekeeping, the full shell and every other page stay.
   */
  isSupervisorHome: boolean;
}

/**
 * What the Housekeeping page offers, from the permission projection and never
 * from a role name (owner 2026-10-19, plan-deskFollowUps T6; GRMS §11.4).
 * Whether a user may pass or fail an inspection is `housekeeping:inspect`.
 */
export function resolveHousekeepingAccess(has: (permission: string) => boolean): HousekeepingAccess {
  const canAssign = has(PERMISSIONS.HOUSEKEEPING_ASSIGN);
  return {
    canAssign,
    isAttendantMode: has(PERMISSIONS.HOUSEKEEPING_WRITE) && !canAssign,
    isSupervisorHome: canAssign && !has(PERMISSIONS.GRMS_WRITE),
  };
}

export type MainNavKey = 'dashboard' | 'activity' | 'access-control' | 'housekeeping';

export interface MainNavEntry {
  key: MainNavKey;
  path: string;
}

/**
 * The sidebar's first group, in order (plan-receptionistDesk F3, R3).
 *
 * Housekeeping is offered to a holder of a housekeeping write, assign or
 * inspect code — `housekeeping:read` alone (the receptionist) is not a reason
 * to visit the page. For the supervisor home Housekeeping leads and the Desk
 * moves to `/dashboard`, since `/` is where the role lands on Housekeeping.
 */
export function sidebarOrder(
  has: (permission: string) => boolean,
  flags: { hasHousekeeping: boolean; showAccessControl: boolean },
): MainNavEntry[] {
  const showHousekeeping = flags.hasHousekeeping && (
    has(PERMISSIONS.HOUSEKEEPING_WRITE)
    || has(PERMISSIONS.HOUSEKEEPING_ASSIGN)
    || has(PERMISSIONS.HOUSEKEEPING_INSPECT)
  );
  const { isSupervisorHome } = resolveHousekeepingAccess(has);
  const housekeeping: MainNavEntry[] = showHousekeeping ? [{ key: 'housekeeping', path: '/housekeeping' }] : [];
  const accessControl: MainNavEntry[] = flags.showAccessControl ? [{ key: 'access-control', path: '/access' }] : [];
  const desk: MainNavEntry = { key: 'dashboard', path: isSupervisorHome ? '/dashboard' : '/' };
  const activity: MainNavEntry = { key: 'activity', path: '/activity' };
  return isSupervisorHome
    ? [...housekeeping, desk, activity, ...accessControl]
    : [desk, activity, ...accessControl, ...housekeeping];
}

/** `isResolving` is true until the projection answers; render nothing role-shaped before then. */
export function useHousekeepingAccess(): HousekeepingAccess & { isResolving: boolean } {
  const permissions = usePermissions();
  return { ...resolveHousekeepingAccess(permissions.has), isResolving: permissions.isLoading };
}

/** Names the signed-in user as the attendant of their own clean; it grants nothing. */
export function getCurrentHousekeepingAttendantId(
  user: UserWithRoleAssignments | null | undefined,
  siteCode: string | null | undefined,
): string | undefined {
  if (!user?.id || !activeRoleCodes(user, siteCode).has('housekeeping_attendant')) {
    return undefined;
  }
  return user.id;
}

export function compareHousekeepingRoomPriority(
  a: HousekeepingRoomPriority,
  b: HousekeepingRoomPriority,
  liveStates: Map<string, LiveHousekeepingPriority>,
  features: GrmsIndicatorFeatures,
): number {
  const aLive = liveStates.get(a.roomCode);
  const bLive = liveStates.get(b.roomCode);
  const { dnd: aDnd, mur: aMur } = resolveGrmsIndicators(a, aLive, features);
  const { dnd: bDnd, mur: bMur } = resolveGrmsIndicators(b, bLive, features);

  if (aMur !== bMur) return aMur ? -1 : 1;
  if (aDnd !== bDnd) return aDnd ? 1 : -1;
  return a.roomCode.localeCompare(b.roomCode);
}
